import { Route, Routes, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { AnimatedPage } from '@/components/AnimatedPage'
import { lazy, Suspense } from 'react'
import { ProcessingProgress } from '@/components/ProcessingProgress'
import { RouteWithTitle } from '@/components/RouteWithTitle'
import { usePageTracking } from '@/hooks/usePageTracking'
import CompareImageAnalyses from '@/pages/CompareImageAnalyses'

// Lazy load components
const Home = lazy(() =>
  import('@/pages/Home').then(module => ({ default: module.Home })),
)
const ImageAnalysis = lazy(() =>
  import('@/pages/ImageAnalysis').then(module => ({
    default: module.ImageAnalysis,
  })),
)
const VideoAnalysis = lazy(() =>
  import('@/pages/VideoAnalysis').then(module => ({
    default: module.VideoAnalysis,
  })),
)
const Pricing = lazy(() =>
  import('@/pages/Pricing').then(module => ({ default: module.Pricing })),
)
const History = lazy(() =>
  import('@/pages/History').then(module => ({ default: module.History })),
)

// Type-safe route parameter types
type RouteParams = {
  imageAnalysisRun: { runId: string }
  videoAnalysisRun: { runId: string }
  compareImageAnalyses: { runIds: string[] }
}

// Type-safe route path builder
type RoutePathBuilder<T extends keyof RouteParams> = {
  path: string
  element: React.ReactElement
  build: (params: RouteParams[T]) => string
  pageTitle?: string
}

// Route configuration with type-safe path builders
export const routes = {
  home: {
    path: '/',
    element: <Home />,
    pageTitle: '',
  },
  video: {
    path: '/video',
    element: <VideoAnalysis />,
    pageTitle: 'Video Analysis',
  },
  image: {
    path: '/image',
    element: <ImageAnalysis />,
    pageTitle: 'Image Analysis',
  },
  pricing: {
    path: '/pricing',
    element: <Pricing />,
    pageTitle: 'Pricing',
  },
  history: {
    path: '/history',
    element: <History />,
    pageTitle: 'History',
  },
  imageAnalysisRun: {
    path: '/image-analysis/:runId',
    element: <ImageAnalysis />,
    build: (params: RouteParams['imageAnalysisRun']) =>
      `/image-analysis/${params.runId}`,
    pageTitle: 'Image Analysis',
  } satisfies RoutePathBuilder<'imageAnalysisRun'>,
  videoAnalysisRun: {
    path: '/video-analysis/:runId',
    element: <VideoAnalysis />,
    build: (params: RouteParams['videoAnalysisRun']) =>
      `/video-analysis/${params.runId}`,
    pageTitle: 'Video Analysis',
  } satisfies RoutePathBuilder<'videoAnalysisRun'>,
  compareImageAnalyses: {
    path: '/compare-image-analyses',
    element: <CompareImageAnalyses />,
    build: (params: RouteParams['compareImageAnalyses']) =>
      `/compare-image-analyses?runIds=${params.runIds.join(',')}`,
    pageTitle: 'Compare Image Analyses',
  } satisfies RoutePathBuilder<'compareImageAnalyses'>,
}

// Utility function for type-safe route building
export const buildRoutePath = <T extends keyof RouteParams>(
  routeKey: T,
  params: RouteParams[T],
): string => {
  const route = routes[routeKey] as RoutePathBuilder<T>
  return route.build(params)
}

export const ProtectedRoutes = () => {
  usePageTracking()
  const location = useLocation()

  return (
    <RouteWithTitle routeConfigs={routes}>
      <AnimatePresence
        mode="wait"
        initial={false}
        onExitComplete={() => window.scrollTo(0, 0)}
      >
        <Routes location={location} key={location.pathname}>
          <Route
            path={routes.home.path}
            element={
              <Suspense fallback={<ProcessingProgress />}>
                <AnimatedPage>{routes.home.element}</AnimatedPage>
              </Suspense>
            }
          />
          <Route
            path={routes.video.path}
            element={
              <Suspense fallback={<ProcessingProgress />}>
                <AnimatedPage>{routes.video.element}</AnimatedPage>
              </Suspense>
            }
          />
          <Route
            path={routes.image.path}
            element={
              <Suspense fallback={<ProcessingProgress />}>
                <AnimatedPage>{routes.image.element}</AnimatedPage>
              </Suspense>
            }
          />
          <Route
            path={routes.pricing.path}
            element={
              <Suspense fallback={<ProcessingProgress />}>
                <AnimatedPage>{routes.pricing.element}</AnimatedPage>
              </Suspense>
            }
          />
          <Route
            path={routes.history.path}
            element={
              <Suspense fallback={<ProcessingProgress />}>
                <AnimatedPage>{routes.history.element}</AnimatedPage>
              </Suspense>
            }
          />
          <Route
            path={routes.imageAnalysisRun.path}
            element={
              <Suspense fallback={<ProcessingProgress />}>
                <AnimatedPage>{routes.imageAnalysisRun.element}</AnimatedPage>
              </Suspense>
            }
          />
          <Route
            path={routes.videoAnalysisRun.path}
            element={
              <Suspense fallback={<ProcessingProgress />}>
                <AnimatedPage>{routes.videoAnalysisRun.element}</AnimatedPage>
              </Suspense>
            }
          />
          <Route
            path={routes.compareImageAnalyses.path}
            element={
              <Suspense fallback={<ProcessingProgress />}>
                <AnimatedPage>
                  {routes.compareImageAnalyses.element}
                </AnimatedPage>
              </Suspense>
            }
          />
        </Routes>
      </AnimatePresence>
    </RouteWithTitle>
  )
}
